import React, { useState } from "react";
import type { TableProps } from "antd";
import { Button, Popconfirm, Space, Table } from "antd";
import moment from "moment";
import axiosClient from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";
import handleApiError from "../../utils/handleError";

interface Exam {
  end_time: number;
  exam_id: string;
  exam_name: string;
  id: string;
  status: string;
  time: number;
  started_at?: number;
  duration?: number;
}

// end_time only becomes a real end once the examiner verifies and the clock starts
// (started_at). Before that the backend holds a provisional worst-case bound there —
// showing it would promise a later finish than the student actually gets — so fall
// back to the expected end for the booked slot.
function sessionEndTime(row: Exam): number {
  if (row.started_at) return row.end_time;
  if (row.duration) return row.time + row.duration * 60;
  return row.end_time;
}

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const MySessionTable: React.FC<any> = ({ data }) => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  async function startTest(exam_instance: any) {
    try {
      const res = await axiosClient.post(
        `examinstances/${exam_instance.id}/start`
      );
      navigate("exam-instructions", {
        state: { exam_instance, saveInstance: res.data },
      });
    } catch (error) {
      handleApiError(error);
    }
  }

  const columns: ColumnsType<Exam> = [
    {
      title: "Exam",
      dataIndex: "exam_name",
    },
    {
      title: "Session Start Time",
      dataIndex: "time",
      render: (time) => `${moment.unix(time).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Session End Time",
      dataIndex: "end_time",
      render: (_, record) =>
        `${moment.unix(sessionEndTime(record)).format("DD/MM/YYYY hh:mmA")}`,
    },

    {
      title: "Actions",
      render: (_, record) => (
        <Space size="middle">
          <Popconfirm
            title="You can only reschedule 2 times"
            description="Are you sure to reschedule?"
            onConfirm={() =>
              navigate(`/schedule-session/reschedule/${record.id}`, {
                state: { scheduleDate: record.time },
              })
            }
            okText="Yes"
            cancelText="No"
          >
            <Button>Reschedule</Button>
          </Popconfirm>
          <Button type="primary" onClick={() => startTest(record)}>
            Start Test
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Table
      style={{ marginTop: 20, whiteSpace: "pre" }}
      columns={columns}
      rowKey={(record) => record.id}
      dataSource={data}
      onChange={(p) =>
        setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
      }
      pagination={pagination}
      rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
    />
  );
};

export default MySessionTable;
